import { useTranslation } from "react-i18next";
import { isGenerationProposal, Turn } from "../../util/slopifyThread";
import { prettyLabel } from "../DynamicField";

interface RestoreConfirmDialogProps {
  turn: Turn;
  currentFormValues: Record<string, unknown>;
  onConfirm: () => void;
  onCancel: () => void;
}

const sortedStringify = (val: unknown): string => {
  if (val === null || typeof val !== "object") return JSON.stringify(val);
  if (Array.isArray(val)) return `[${val.map(sortedStringify).join(",")}]`;
  const sorted = Object.keys(val as Record<string, unknown>)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${sortedStringify((val as Record<string, unknown>)[k])}`);
  return `{${sorted.join(",")}}`;
};

const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return false;
  try {
    return sortedStringify(a) === sortedStringify(b);
  } catch {
    return false;
  }
};

export const RestoreConfirmDialog = ({
  turn,
  currentFormValues,
  onConfirm,
  onCancel,
}: RestoreConfirmDialogProps) => {
  const { t } = useTranslation();

  const proposal = isGenerationProposal(turn.proposal) ? turn.proposal : null;
  const overwrittenFieldNames = (proposal?.fields ?? [])
    .filter((f) => !deepEqual(f.before, currentFormValues?.[f.fieldName]))
    .map((f) => f.fieldName);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="restore-confirm-title"
    >
      <div className="bg-white rounded-lg shadow-lg p-4 w-full max-w-sm mx-3">
        <h3 id="restore-confirm-title" className="text-sm font-semibold text-gray-800 mb-2">
          {t("general.assistantScenarIa.history.restoreConfirm.title")}
        </h3>
        <p className="text-xs text-gray-600 mb-3">
          {t("general.assistantScenarIa.history.restoreConfirm.body")}
        </p>
        {overwrittenFieldNames.length > 0 ? (
          <div className="mb-3">
            <div className="text-[11px] font-medium text-amber-600 mb-1">
              {t("general.assistantScenarIa.history.restoreConfirm.overwriteHeader", {
                count: overwrittenFieldNames.length,
              })}
            </div>
            <ul className="text-[11px] text-gray-700 list-disc list-inside max-h-32 overflow-y-auto">
              {overwrittenFieldNames.map((name) => (
                <li key={name}>{prettyLabel(name, t as any)}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="mb-3 text-[11px] text-gray-500">
            {t("general.assistantScenarIa.history.restoreConfirm.noOverwrites")}
          </div>
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded-md text-gray-600 hover:bg-gray-100"
            onClick={onCancel}
          >
            {t("general.assistantScenarIa.history.restoreConfirm.cancel")}
          </button>
          <button
            type="button"
            className="text-xs px-3 py-1.5 rounded-md bg-green-100 text-green-700 border border-green-200 hover:bg-green-200"
            onClick={onConfirm}
          >
            {t("general.assistantScenarIa.history.restoreConfirm.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestoreConfirmDialog;
